import { useState } from 'react'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import { Plus } from 'lucide-react'
import BatchFormWizard from '../BatchFormWizard'
import useMutationCreateBatch from '../../api/hooks/useMutationCreateBatch'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { useQueryClient } from '@tanstack/react-query'
import { BatchesQueryKeys } from '../../keys'
import { CreateBatchRequest } from '../../type'
import useQueryProfile from '@/modules/auth/api/hooks/useQueryProfile'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { CouponType } from '@/schema'

interface CreateBatchDialogProps {
	trigger?: React.ReactNode
}

export default function CreateBatchDialog({ trigger }: CreateBatchDialogProps) {
	const [open, setOpen] = useState(false)
	const createBatch = useMutationCreateBatch()
	const queryClient = useQueryClient()
	const { data: user } = useQueryProfile()

	const hasWritePermission = user?.userGroup.writePermission.includes(
		PermissionKeys.MANAGE_COUPON,
	)

	if (!hasWritePermission) return null

	const defaultTrigger = (
		<Button>
			<Plus className="mr-2 h-4 w-4" />
			สร้างชุดคูปอง
		</Button>
	)

	const handleSubmit = async (values: {
		name: string
		description?: string
		quantity?: number
		promotionId?: string
		creditAmount?: number
		maxQuota?: number | null
		type?: CouponType
		customCode?: string
		prefix?: string
		requireConnectedLine?: boolean
	}) => {
		try {
			const isSingleType = values.type === CouponType.SINGLE

			// Build payload conditionally - don't include undefined fields
			const payload: Record<string, unknown> = {
				batchName: values.name,
				batchDescription: values.description,
				quantity: values.quantity || 1,
				type: values.type || CouponType.MULTIPLE,
				requireConnectedLine: values.requireConnectedLine ?? false,
			}

			// Add promotionId or creditAmount (mutually exclusive)
			if (values.promotionId) {
				payload.promotionId = values.promotionId
			}
			if (values.creditAmount) {
				payload.creditAmount = values.creditAmount
			}

			// Add maxQuota only if it has a value
			if (values.maxQuota !== null && values.maxQuota !== undefined) {
				payload.maxQuota = values.maxQuota
			}

			// Add customCode only for SINGLE type
			if (isSingleType && values.customCode) {
				payload.customCode = values.customCode
			}

			// Add prefix only for MULTIPLE type
			if (!isSingleType && values.prefix) {
				payload.prefix = values.prefix
			}

			await createBatch.mutateAsync(payload as CreateBatchRequest)
			setOpen(false)
			toast.success('สร้างชุดคูปองสำเร็จ')
			queryClient.invalidateQueries({
				queryKey: [BatchesQueryKeys.GetBatches],
			})
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	return (
		<>
			<div onClick={() => setOpen(true)}>{trigger || defaultTrigger}</div>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[650px]">
					<DialogHeader>
						<DialogTitle>สร้างคูปอง</DialogTitle>
					</DialogHeader>
					<BatchFormWizard
						onSubmit={handleSubmit}
						onCancel={() => setOpen(false)}
						isSubmitting={createBatch.isPending}
					/>
				</DialogContent>
			</Dialog>
		</>
	)
}
